
import * as Sentry from '@/lib/sentry-shim';
import { supabase, isMissingCredentials } from '@/lib/supabase';

/**
 * Get the site origin URL for email redirects.
 * For email links (password reset, email confirmation, etc.), we always want
 * to redirect to production, not localhost - even if the user requested the
 * email from a local development environment.
 * 
 * @returns The production site URL or the environment variable if set
 */
const getSiteOrigin = (): string => {
  // Check for explicit site URL in environment variable first
  if (import.meta.env.VITE_SITE_URL) {
    return import.meta.env.VITE_SITE_URL.replace(/\/$/, ''); // Remove trailing slash
  }
  
  // Fallback to production URL.
  // This ensures email links always point to production, not localhost.
  // Migrated from app.discloser.co → www.discloser.co on 2026-04-30 as part
  // of the single-origin consolidation (see docs/runbooks/domain-consolidation.md).
  return 'https://www.discloser.co';
};

export const signInWithPassword = async (email: string, password: string) => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing authentication');
    return;
  }

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    // Report to Sentry
    Sentry.captureException(error, {
      tags: {
        function: 'signInWithPassword',
        source: 'supabase',
      },
      extra: {
        email,
        errorCode: error.code,
        errorMessage: error.message,
        errorStatus: error.status,
      },
    });
    throw error;
  }
};

export interface SignUpResult {
  needsVerification: boolean;
  email: string;
}

export const signUpWithPassword = async (email: string, password: string, metadata = {}): Promise<SignUpResult> => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing authentication');
    return { needsVerification: false, email };
  }

  // Check if a profile with this email already exists
  console.log('signUpWithPassword: Checking if email already exists in profiles:', email);
  const { data: existingProfile, error: profileCheckError } = await supabase
    .from('profiles')
    .select('id, email, created_at')
    .eq('email', email)
    .maybeSingle();

  console.log('signUpWithPassword: Profile check result:', {
    found: !!existingProfile,
    profileId: existingProfile?.id,
    error: profileCheckError?.message,
  });

  if (existingProfile) {
    const duplicateError: any = new Error('User already registered');
    duplicateError.code = 'user_already_registered';
    duplicateError.message = 'This email is already registered. Please sign in instead or use the "Forgot Password" link.';
    duplicateError.status = 400;
    Sentry.captureException(duplicateError, {
      tags: { function: 'signUpWithPassword', source: 'profiles_table_check', issue: 'duplicate_email_profile_exists' },
      extra: { email, existingProfileId: existingProfile.id },
    });
    throw duplicateError;
  }

  console.log('signUpWithPassword: Calling Supabase auth.signUp for', email);

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata
    },
  });

  console.log('signUpWithPassword: Full response:', {
    hasUser: !!data.user,
    hasSession: !!data.session,
    userId: data.user?.id,
    userEmail: data.user?.email,
    userCreatedAt: data.user?.created_at,
    userConfirmedAt: data.user?.email_confirmed_at,
    hasError: !!error,
    errorCode: error?.code,
    errorMessage: error?.message,
  });

  if (error) {
    console.error('signUpWithPassword: Error from Supabase', error);
    Sentry.captureException(error, {
      tags: { function: 'signUpWithPassword', source: 'supabase' },
      extra: { email, errorCode: error.code, errorMessage: error.message, errorStatus: error.status },
    });
    throw error;
  }

  if (!data.user) {
    console.warn('signUpWithPassword: No user returned, likely duplicate email:', email);
    const duplicateError: any = new Error('User already registered');
    duplicateError.code = 'user_already_registered';
    duplicateError.message = 'This email is already registered. Please sign in instead or use the "Forgot Password" link.';
    duplicateError.status = 400;
    Sentry.captureException(duplicateError, {
      tags: { function: 'signUpWithPassword', source: 'supabase', issue: 'duplicate_email_no_user' },
      extra: { email },
    });
    throw duplicateError;
  }

  // Duplicate detection: with autoconfirm OFF, new signups return user + no session + email_confirmed_at: null.
  // Existing confirmed users also return user + no session, but HAVE email_confirmed_at set.
  // Old unconfirmed users have a created_at far in the past.
  const userCreatedAt = data.user.created_at ? new Date(data.user.created_at).getTime() : null;
  const now = Date.now();
  const userAgeMs = userCreatedAt ? now - userCreatedAt : null;

  // If user has a confirmed email, they already verified previously — this is a duplicate
  if (data.user.email_confirmed_at) {
    console.warn('signUpWithPassword: User already has confirmed email, duplicate signup:', email);
    const duplicateError: any = new Error('User already registered');
    duplicateError.code = 'user_already_registered';
    duplicateError.message = 'This email is already registered. Please sign in instead or use the "Forgot Password" link.';
    duplicateError.status = 400;
    Sentry.captureException(duplicateError, {
      tags: { function: 'signUpWithPassword', source: 'supabase', issue: 'duplicate_email_confirmed_user' },
      extra: { email, emailConfirmedAt: data.user.email_confirmed_at, userAgeMs },
    });
    throw duplicateError;
  }

  // If user was created more than 2 minutes ago and still unconfirmed, it's likely a re-signup
  // Supabase re-sends the OTP in this case, which is fine — treat it as needing verification
  if (userAgeMs !== null && userAgeMs > 120000 && !data.session) {
    console.log('signUpWithPassword: Older unconfirmed user re-signing up, re-sending OTP:', email);
    return { needsVerification: true, email };
  }

  // New signup with no session = needs email OTP verification
  if (!data.session) {
    console.log('signUpWithPassword: New signup, needs email verification:', email);
    return { needsVerification: true, email };
  }

  // If we got a session (autoconfirm is on), no verification needed
  console.log('signUpWithPassword: Success with session (autoconfirm on):', {
    userId: data.user.id,
    email: data.user.email,
  });
  return { needsVerification: false, email };
};

export const verifyEmailOtp = async (email: string, token: string) => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing OTP verification');
    return;
  }

  console.log('verifyEmailOtp: Verifying OTP for', email);
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'signup',
  });

  if (error) {
    console.error('verifyEmailOtp: Error from Supabase', error);
    Sentry.captureException(error, {
      tags: { function: 'verifyEmailOtp', source: 'supabase' },
      extra: { email, errorCode: error.code, errorMessage: error.message, errorStatus: error.status },
    });
    throw error;
  }

  console.log('verifyEmailOtp: Success!', {
    userId: data.user?.id,
    hasSession: !!data.session,
  });
};

export const resendConfirmationEmail = async (email: string) => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing resend confirmation');
    return;
  }
  console.log('resendConfirmationEmail: Attempting to resend confirmation to:', email);
  const { error } = await (supabase as any).auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: `${getSiteOrigin()}/onboarding`,
    },
  });
  if (error) {
    console.error('resendConfirmationEmail: Error from Supabase:', error);
    
    // Report to Sentry
    Sentry.captureException(error, {
      tags: {
        function: 'resendConfirmationEmail',
        source: 'supabase',
      },
      extra: {
        email,
        errorCode: error.code,
        errorMessage: error.message,
        errorStatus: error.status,
      },
    });
    
    throw error;
  }
  console.log('resendConfirmationEmail: Success! Resent confirmation email to:', email);
};

export const signOutUser = async () => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing authentication');
    return;
  }

  const { error } = await supabase.auth.signOut();
  if (error) {
    // Report to Sentry
    Sentry.captureException(error, {
      tags: {
        function: 'signOutUser',
        source: 'supabase',
      },
      extra: {
        errorCode: error.code,
        errorMessage: error.message,
        errorStatus: error.status,
      },
    });
    throw error;
  }
};

export const resetUserPassword = async (email: string) => {
  if (isMissingCredentials()) {
    console.log('Development mode: Bypassing authentication');
    return;
  }

  const redirectUrl = `${getSiteOrigin()}/update-password`;
  console.log('resetUserPassword: Attempting to send password reset email to:', email);
  console.log('resetUserPassword: Redirect URL will be:', redirectUrl);

  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectUrl,
  });

  if (error) {
    console.error('resetUserPassword: Error from Supabase:', error);
    
    // Report to Sentry
    Sentry.captureException(error, {
      tags: {
        function: 'resetUserPassword',
        source: 'supabase',
      },
      extra: {
        email,
        errorCode: error.code,
        errorMessage: error.message,
        errorStatus: error.status,
      },
    });
    
    throw error;
  }

  console.log('resetUserPassword: Success! Password reset email sent to:', email);
  // Note: Supabase doesn't return data for resetPasswordForEmail, but if no error, it succeeded
};
